import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { SegmentoApi } from 'app/domains/environment/segmentos/apis/segmento.api';
import { EMPTY } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { ApiError } from '../../../../../app/core/errors/api-error';
import { MessageService } from '../../../../../app/core/services/message/message.service';
import { ISegmentoFilter } from '../../../environment/segmentos/interfaces/segmento-filter.interface';
import { ISegmentoResponse } from '../../../environment/segmentos/interfaces/segmento-response.interface';
import { ISegmento } from '../../../environment/segmentos/interfaces/segmento.interface';
import { EnumSituacao } from '../../enums/situacao.enum';
import { CustomControlValueAccessor } from '../../utils/custom-control-value-accessor/custom-control-value-accessor';
import { IGroup } from '../multi-select-grouped/multi-select-grouped.component';
import { IMultiSelectSettings } from '../multi-select/models/multi-select-settings.interface';
import { EnumTipoSegmento } from './enums/segmento-tipo.enum';
import { SegmentoSelectorStoreService } from './service/segmento-selector-store-service';

@Component({
  selector: 'sgt-segmentos-multi-select',
  templateUrl: './segmentos-multi-select.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SegmentosMultiSelectComponent),
      multi: true
    }
  ]
})
export class SegmentosMultiSelectComponent
  extends CustomControlValueAccessor<number[]>
  implements OnInit {
  @Input() situacao: EnumSituacao = EnumSituacao.ATIVO;
  @Input() segmento: EnumTipoSegmento;
  @Input() clearFields = false;
  @Input() variante: 'filtro' | undefined;
  @Input() icon = 'fa-tags';
  @Input() settings: Partial<IMultiSelectSettings> = {};

  @Output() changed = new EventEmitter<number[]>();

  segmentos: IGroup[] = [];
  selectedSegmentIds: number[] = [];

  defaultSettings: IMultiSelectSettings = {
    placeholder: '',
    singular: '',
    plural: '',
    isDisabled: false,
    isLoading: false,
    showFooter: true,
    searchEmptyResultMessage: '',
    showCountSelected: true
  };

  constructor(
    private messageService: MessageService,
    private segmentoApi: SegmentoApi,
    private segmentoSelectorStoreService: SegmentoSelectorStoreService
  ) {
    super();
  }

  ngOnInit() {
    const SEGMENTO_LABELS = {
      [EnumTipoSegmento.CLIENTE]: { singular: 'cliente', plural: 'clientes' },
      [EnumTipoSegmento.ITEM]: { singular: 'item', plural: 'itens' }
    };
    const labels = SEGMENTO_LABELS[this.segmento] ?? {
      singular: 'segmento',
      plural: 'segmentos'
    };

    this.settings = {
      ...this.defaultSettings,
      placeholder: `Segmentos de ${labels.plural}`,
      singular: `Segmento de ${labels.singular}`,
      plural: `Segmentos de ${labels.plural}`,
      searchEmptyResultMessage: `Nenhum segmento de ${labels.singular} encontrado`,
      ...this.settings
    };
  }

  hasOpenedFirstTime(isFirstTime: boolean): void {
    if (isFirstTime && this.segmentos.length === 0) {
      this.fetchSegments().subscribe();
    }
  }

  private fetchSegments() {
    if (this.settings.isLoading || this.segmentos.length > 0) {
      return EMPTY;
    }

    this.settings = { ...this.settings, isLoading: true };

    const filter: Partial<ISegmentoFilter> = {
      situacoes: this.situacao,
      cadastrosTiposClassificacoes: [this.segmento]
    };

    return this.segmentoApi.fetchList(filter).pipe(
      take(1),
      tap({
        next: (response: ISegmentoResponse) => {
          this.segmentoSelectorStoreService.setSegmentoSelectorStore(
            response.data
          );
          this.segmentos = [
            this.optionalOption(),
            ...this.convertDTOtoModel(response.data)
          ];
          this.settings = { ...this.settings, isLoading: false };
        },
        error: (error: ApiError) => {
          this.settings = { ...this.settings, isLoading: false };
          this.messageService.error(
            error.message,
            'Erro ao pesquisar segmentos',
            error
          );
        }
      })
    );
  }

  private convertDTOtoModel(segmentos: ISegmento[]): IGroup[] {
    const map = new Map<number, IGroup>();

    segmentos.forEach((item) => {
      if (!map.has(item.idTipoClassificacao)) {
        map.set(item.idTipoClassificacao, {
          id: item.idTipoClassificacao,
          name: item.nomeTipoClassificacao,
          options: []
        });
      }

      map.get(item.idTipoClassificacao)!.options!.push({
        id: item.id,
        name: item.nome,
        isChecked: (this.value ?? []).includes(item.id)
      });
    });

    return Array.from(map.values());
  }

  private optionalOption(): IGroup {
    return {
      id: 0,
      name: 'OUTROS',
      options: [
        {
          id: 0,
          name: 'SEM SEGMENTO',
          isChecked: (this.value ?? []).includes(0)
        }
      ]
    };
  }

  setSelectedSegments(ids: number[]): void {
    const safeIds = ids ?? [];
    this.selectedSegmentIds = safeIds;
    this.segmentos.forEach((group) => {
      group.options?.forEach((option) => {
        option.isChecked = safeIds.includes(option.id);
      });
    });
    if (safeIds.length > 0 && this.segmentos.length === 0) {
      this.fetchSegments().subscribe();
    }
  }

  clearSelection(): void {
    this.selectedSegmentIds = [];
    this.segmentos.forEach((group) => {
      group.options?.forEach((option) => {
        option.isChecked = false;
      });
    });
  }

  onSearch(): void {
    const ids = this.getSelectedIds();
    this.emitChange(ids);
    this.markAsTouched();
    this.changed.emit(ids);
  }

  onSelectionChange(): void {
    this.emitChange(this.selectedSegmentIds ?? []);
    this.markAsTouched();
  }

  private getSelectedIds(): number[] {
    const ids: number[] = [];
    this.segmentos.forEach((group) => {
      group.options?.forEach((option) => {
        if (option.isChecked) {
          ids.push(option.id);
        }
      });
    });
    return ids;
  }

  protected updateValue(value: number[] | null): void {
    this.setSelectedSegments(value ?? []);
  }

  protected onDisabledStateChange(isDisabled: boolean): void {
    this.settings = {
      ...this.settings,
      isDisabled
    };
  }
}
